import { App, Button, DatePicker, Radio, Select, Switch } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { CustomTimePicker } from "../../../components/Date&TimePickers/CustomTimePicker.tsx"
import { TextArea } from "../../../components/Input/TextArea.tsx"
import { TextInput } from "../../../components/Input/TextInput.tsx"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import utils from "../../../utils/utils.ts"
import { friendlyContactRole } from "../helpers/contact-role.ts"
import { resolveGcContact } from "../helpers/recipient.ts"
import { createCalendarEventThunk, updateCalendarEventThunk } from "../redux/portal.thunks.ts"
import type { PortalCalendarEvent, PortalProjectManager } from "../types/portal.types.ts"
import { PortalResponsiveModal } from "./PortalResponsiveModal.tsx"

type EventKind = "appointment" | "reminder"

const REMINDER_OFFSET_OPTIONS = [5, 10, 15, 30, 60, 1440]

interface CalendarEventFormModalProps {
  open: boolean
  onClose: () => void
  /** Prefills the date (and, when given, the start time) — a Month day
   * click or a Week time-slot click. Ignored in edit mode. */
  initialDate?: Dayjs | null
  /** Preselects and locks the project in All-projects mode only when the
   * caller is already inside a single project's context. Ignored in edit
   * mode (a customer-created appointment's project cannot change). */
  initialProjectId?: string | null
  /** Present → edit an existing event of this customer's own instead of
   * creating a new one. `kind` is read from `event_kind` and cannot be
   * changed — the server infers it from whether an attendee already
   * exists, so switching a saved reminder into an appointment (or back)
   * has no supported path here. */
  editingEvent?: PortalCalendarEvent | null
}

const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone

const toUtcMidnight = (date: Dayjs) => dayjs.utc(date.format("YYYY-MM-DD"))

export function CalendarEventFormModal({
  open,
  onClose,
  initialDate,
  initialProjectId,
  editingEvent
}: CalendarEventFormModalProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { message } = App.useApp()
  const projects = useAppSelector((state) => state.portal.projects)

  const isEdit = !!editingEvent

  const [kind, setKind] = useState<EventKind>("appointment")
  const [projectId, setProjectId] = useState<string | null>(null)
  const [title, setTitle] = useState("")
  const [allDay, setAllDay] = useState(false)
  const [date, setDate] = useState<Dayjs>(dayjs())
  const [startTime, setStartTime] = useState<Dayjs>(dayjs().minute(0).second(0))
  const [endTime, setEndTime] = useState<Dayjs>(dayjs().add(1, "hour").minute(0).second(0))
  const [location, setLocation] = useState("")
  const [meetingLink, setMeetingLink] = useState("")
  const [description, setDescription] = useState("")
  const [participantIds, setParticipantIds] = useState<string[]>([])
  const [externalEmails, setExternalEmails] = useState<string[]>([])
  const [emailDraft, setEmailDraft] = useState("")
  const [reminderOffsetMinutes, setReminderOffsetMinutes] = useState<number | null>(5)
  const [remindAtStart, setRemindAtStart] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Re-derive the form's starting values every time the dialog opens fresh
  // for a *different* target (a new day click, or a different event to
  // edit) — never on every render, or an in-progress edit would keep
  // getting stomped back to its original values.
  useEffect(() => {
    if (!open) return
    if (editingEvent) {
      setKind(editingEvent.event_kind === "private_reminder" ? "reminder" : "appointment")
      setProjectId(editingEvent.project_id)
      setTitle(editingEvent.title)
      setAllDay(editingEvent.all_day)
      setDate(dayjs(editingEvent.start_at))
      setStartTime(dayjs(editingEvent.start_at))
      setEndTime(dayjs(editingEvent.end_at))
      setLocation(editingEvent.location ?? "")
      setMeetingLink(editingEvent.meeting_link ?? "")
      setDescription(editingEvent.description ?? "")
      setParticipantIds(
        editingEvent.attendees.filter((a) => a.kind === "internal" && a.id).map((a) => a.id as string)
      )
      setExternalEmails(
        editingEvent.attendees.filter((a) => a.kind === "external").map((a) => a.email)
      )
      setReminderOffsetMinutes(editingEvent.reminder?.offset_minutes ?? null)
      setRemindAtStart(editingEvent.reminder?.remind_at_start ?? true)
    } else {
      const base = initialDate ?? dayjs()
      const project = projects.find((p) => p.id === (initialProjectId ?? null)) ?? null
      const primaryContact = project ? resolveGcContact(project.project_managers) : undefined
      setKind("appointment")
      setProjectId(initialProjectId ?? null)
      setTitle("")
      setAllDay(false)
      setDate(base)
      setStartTime(base)
      setEndTime(base.add(1, "hour"))
      setLocation("")
      setMeetingLink("")
      setDescription("")
      setParticipantIds(primaryContact ? [primaryContact.id] : [])
      setExternalEmails([])
      setReminderOffsetMinutes(5)
      setRemindAtStart(true)
    }
    setEmailDraft("")
    setError(null)
    // Depend on the id, not the whole object: `editingEvent` is a fresh
    // object reference on every parent render (CalendarEventDetailModal
    // rebuilds it from the live store each time), and resetting the form on
    // every such render would wipe an in-progress edit the instant anything
    // else in the store changed.
  }, [open, editingEvent?.id, initialDate, initialProjectId])

  const selectedProject = projects.find((p) => p.id === projectId) ?? null
  const projectTeam: PortalProjectManager[] = selectedProject?.project_managers ?? []

  const handleClose = () => {
    if (submitting) return
    onClose()
  }

  // "Changing the project ... never silently invite a different project
  // team": switching projects revalidates the current selection against
  // the *new* project's roster — a contact not assigned there is dropped,
  // never carried over — and re-defaults to the new project's primary
  // contact so the field is never left empty by surprise. Create-mode
  // only: an existing appointment's project can't change (`partial_update`
  // never accepts `project_id`).
  const handleProjectChange = (newProjectId: string) => {
    setProjectId(newProjectId)
    const newProject = projects.find((p) => p.id === newProjectId) ?? null
    const newTeamIds = new Set((newProject?.project_managers ?? []).map((m) => m.id))
    setParticipantIds((current) => {
      const stillEligible = current.filter((id) => newTeamIds.has(id))
      if (stillEligible.length > 0) return stillEligible
      const primaryContact = newProject ? resolveGcContact(newProject.project_managers) : undefined
      return primaryContact ? [primaryContact.id] : []
    })
  }

  const emailAlreadyUsed = (email: string) => {
    const normalized = email.toLowerCase()
    if (externalEmails.some((existing) => existing.toLowerCase() === normalized)) return true
    return projectTeam.some(
      (member) => participantIds.includes(member.id) && member.email.toLowerCase() === normalized
    )
  }

  const handleAddEmail = () => {
    const candidate = emailDraft.trim().toLowerCase()
    if (!candidate) return
    if (!utils.validateEmail(candidate)) {
      setError(t("portal.calendar.create.invalidEmail"))
      return
    }
    if (emailAlreadyUsed(candidate)) {
      setError(t("portal.calendar.create.duplicateGuest"))
      return
    }
    setError(null)
    setExternalEmails((current) => [...current, candidate])
    setEmailDraft("")
  }

  const handleRemoveEmail = (email: string) => {
    setExternalEmails((current) => current.filter((e) => e !== email))
  }

  const toggleParticipant = (id: string) => {
    setParticipantIds((current) =>
      current.includes(id) ? current.filter((p) => p !== id) : [...current, id]
    )
  }

  const handleSubmit = async () => {
    setError(null)
    if (!title.trim()) {
      setError(t("portal.calendar.create.titleRequired"))
      return
    }
    if (kind === "appointment" && !isEdit && !projectId) {
      setError(t("portal.calendar.create.projectRequired"))
      return
    }
    if (kind === "appointment" && !isEdit && participantIds.length === 0) {
      setError(t("portal.calendar.create.participantsRequired"))
      return
    }
    if (!allDay && !endTime.isAfter(startTime)) {
      setError(t("portal.calendar.create.endAfterStart"))
      return
    }

    const startAt = allDay
      ? toUtcMidnight(date).toISOString()
      : date.hour(startTime.hour()).minute(startTime.minute()).second(0).millisecond(0).toISOString()
    const endAt = allDay
      ? toUtcMidnight(date).add(1, "day").toISOString()
      : date.hour(endTime.hour()).minute(endTime.minute()).second(0).millisecond(0).toISOString()

    setSubmitting(true)

    if (isEdit && editingEvent) {
      const isOwnAppointment = editingEvent.event_kind === "contractor_appointment"
      const result = await dispatch(
        updateCalendarEventThunk({
          eventId: editingEvent.id,
          payload: {
            title: title.trim(),
            start_at: startAt,
            end_at: endAt,
            all_day: allDay,
            timezone,
            location: location.trim() || undefined,
            meeting_link: meetingLink.trim() || undefined,
            description: description.trim() || undefined,
            // Team/guest edits only apply to an appointment — a reminder
            // has no participants to diff, and the server rejects any sent
            // for one.
            ...(isOwnAppointment
              ? { participant_ids: participantIds, external_emails: externalEmails }
              : {}),
            reminder_offset_minutes: reminderOffsetMinutes,
            remind_at_start: remindAtStart
          }
        })
      )
      setSubmitting(false)
      if (updateCalendarEventThunk.fulfilled.match(result)) {
        message.success(t("portal.calendar.create.updateSuccessToast"))
        onClose()
      } else {
        setError(t("portal.calendar.create.updateFailedToast"))
      }
      return
    }

    const result = await dispatch(
      createCalendarEventThunk({
        kind,
        title: title.trim(),
        start_at: startAt,
        end_at: endAt,
        all_day: allDay,
        timezone,
        location: location.trim() || undefined,
        meeting_link: meetingLink.trim() || undefined,
        description: description.trim() || undefined,
        project_id: projectId,
        participant_ids: kind === "appointment" ? participantIds : [],
        external_emails: kind === "appointment" ? externalEmails : [],
        reminder_offset_minutes: reminderOffsetMinutes,
        remind_at_start: remindAtStart
      })
    )
    setSubmitting(false)
    if (createCalendarEventThunk.fulfilled.match(result)) {
      message.success(
        kind === "appointment"
          ? t("portal.calendar.create.createAppointmentSuccessToast")
          : t("portal.calendar.create.createReminderSuccessToast")
      )
      onClose()
    } else {
      setError(result.payload?.message || t("portal.calendar.create.createFailedToast"))
    }
  }

  const selectedTeamNames = projectTeam
    .filter((member) => participantIds.includes(member.id))
    .map((member) => [member.first_name, member.last_name].filter(Boolean).join(" "))
  const invitees = [...selectedTeamNames, ...externalEmails]

  return (
    <PortalResponsiveModal
      open={open}
      onClose={handleClose}
      title={
        isEdit ? t("portal.calendar.create.editTitle") : t("portal.calendar.create.createTitle")
      }
      closable={!submitting}
      maskClosable={!submitting}
    >
      <div className="flex flex-col gap-4">
        {!isEdit && (
          <Radio.Group
            value={kind}
            onChange={(e) => setKind(e.target.value as EventKind)}
            disabled={submitting}
            className="flex flex-col gap-2"
          >
            <Radio value="appointment" className="items-start [&>span:last-child]:w-full">
              <div className="-mt-0.5">
                <p className="font-medium text-gray-900">
                  {t("portal.calendar.create.typeAppointment")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("portal.calendar.create.typeAppointmentDescription")}
                </p>
              </div>
            </Radio>
            <Radio value="reminder" className="items-start [&>span:last-child]:w-full">
              <div className="-mt-0.5">
                <p className="font-medium text-gray-900">
                  {t("portal.calendar.create.typeReminder")}
                </p>
                <p className="text-xs text-gray-500">
                  {t("portal.calendar.create.typeReminderDescription")}
                </p>
              </div>
            </Radio>
          </Radio.Group>
        )}

        {!isEdit && (
          <div>
            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              {t("portal.calendar.create.projectLabel")}
            </label>
            <Select
              className="w-full"
              placeholder={t("portal.calendar.create.projectPlaceholder")}
              value={projectId ?? undefined}
              onChange={handleProjectChange}
              disabled={submitting}
              allowClear={kind === "reminder"}
              options={projects.map((p) => ({ value: p.id, label: p.lead_name }))}
            />
          </div>
        )}

        {kind === "appointment" && (
          <div className="flex flex-col gap-3 rounded-md border border-gray-100 p-3">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
                {t("portal.calendar.create.projectTeamLabel")}
              </label>
              {projectTeam.length === 0 ? (
                <p className="text-sm text-gray-400">{t("portal.calendar.create.recipientNone")}</p>
              ) : (
                <div className="flex flex-col gap-1.5">
                  {projectTeam.map((member) => (
                    <label
                      key={member.id}
                      className="flex items-center gap-2 rounded px-1 py-1 text-sm hover:bg-gray-50"
                    >
                      <input
                        type="checkbox"
                        checked={participantIds.includes(member.id)}
                        onChange={() => toggleParticipant(member.id)}
                        disabled={submitting}
                        className="size-4"
                      />
                      <span className="text-gray-900">
                        {[member.first_name, member.last_name].filter(Boolean).join(" ")}
                      </span>
                      <span className="text-xs text-gray-400">
                        {friendlyContactRole(member.role)}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
                {t("portal.calendar.create.externalGuestsLabel")}
              </label>
              <div className="flex gap-2">
                <TextInput
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  onPressEnter={(e) => {
                    e.preventDefault()
                    handleAddEmail()
                  }}
                  placeholder={t("portal.calendar.create.externalGuestsPlaceholder")}
                  disabled={submitting}
                  disableAutoCapitalize
                />
                <Button onClick={handleAddEmail} disabled={submitting}>
                  {t("portal.calendar.create.addGuest")}
                </Button>
              </div>
              {externalEmails.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {externalEmails.map((email) => (
                    <span
                      key={email}
                      className="flex items-center gap-1.5 rounded-full bg-gray-100 py-1 pl-3 pr-2 text-sm text-gray-700"
                    >
                      {email}
                      <button
                        type="button"
                        onClick={() => handleRemoveEmail(email)}
                        disabled={submitting}
                        aria-label={t("portal.calendar.create.removeGuest", { email })}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
            {t("portal.calendar.create.titleLabel")}
          </label>
          <TextInput
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("portal.calendar.create.titlePlaceholder")}
            disabled={submitting}
          />
        </div>

        <div className="flex items-center gap-2">
          <Switch checked={allDay} onChange={setAllDay} disabled={submitting} />
          <span className="text-sm text-gray-700">{t("portal.calendar.create.allDayLabel")}</span>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="min-w-[140px] flex-1">
            <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
              {t("portal.calendar.create.dateLabel")}
            </label>
            <DatePicker
              className="w-full"
              value={date}
              onChange={(value) => value && setDate(value)}
              disabled={submitting}
              allowClear={false}
            />
          </div>
          {!allDay && (
            <>
              <div className="min-w-[120px] flex-1">
                <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
                  {t("portal.calendar.create.startTimeLabel")}
                </label>
                <CustomTimePicker
                  className="w-full"
                  value={startTime}
                  onChange={(value) => value && setStartTime(value)}
                  disabled={submitting}
                  allowClear={false}
                />
              </div>
              <div className="min-w-[120px] flex-1">
                <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
                  {t("portal.calendar.create.endTimeLabel")}
                </label>
                <CustomTimePicker
                  className="w-full"
                  value={endTime}
                  onChange={(value) => value && setEndTime(value)}
                  disabled={submitting}
                  allowClear={false}
                />
              </div>
            </>
          )}
        </div>

        {!allDay && (
          <p className="-mt-2 text-xs text-gray-400">
            {t("portal.calendar.create.timezoneLabel")}: {timezone}
          </p>
        )}

        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
            {t("portal.calendar.create.locationLabel")}
          </label>
          <TextInput
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder={t("portal.calendar.create.locationPlaceholder")}
            disabled={submitting}
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
            {t("portal.calendar.create.meetingLinkLabel")}
          </label>
          <TextInput
            value={meetingLink}
            onChange={(e) => setMeetingLink(e.target.value)}
            placeholder={t("portal.calendar.create.meetingLinkPlaceholder")}
            disabled={submitting}
            disableAutoCapitalize
          />
        </div>

        <div>
          <label className="mb-1 block text-xs font-medium uppercase text-gray-400">
            {t("portal.calendar.create.descriptionLabel")}
          </label>
          <TextArea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t("portal.calendar.create.descriptionPlaceholder")}
            disabled={submitting}
          />
        </div>

        <div className="flex flex-col gap-2 rounded-md border border-gray-100 p-3">
          <p className="text-xs font-medium uppercase text-gray-400">
            {t("portal.calendar.create.remindersLabel")}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-700">{t("portal.calendar.create.beforeMeeting")}</span>
            <Select
              className="w-40"
              value={reminderOffsetMinutes}
              onChange={setReminderOffsetMinutes}
              disabled={submitting}
              options={[
                { value: null, label: t("portal.calendar.create.reminderNone") },
                ...REMINDER_OFFSET_OPTIONS.map((minutes) => ({
                  value: minutes,
                  label:
                    minutes < 60
                      ? t("portal.calendar.create.reminderMinutes", { count: minutes })
                      : minutes === 60
                        ? t("portal.calendar.create.reminderHour")
                        : t("portal.calendar.create.reminderDay")
                }))
              ]}
            />
          </div>
          <label className="flex items-center gap-2">
            <Switch checked={remindAtStart} onChange={setRemindAtStart} disabled={submitting} />
            <span className="text-sm text-gray-700">{t("portal.calendar.create.remindAtStart")}</span>
          </label>
          <p className="text-xs text-gray-400">{t("portal.calendar.create.reminderPersonalNote")}</p>
        </div>

        {(kind === "reminder" || invitees.length > 0 || selectedProject) && (
          <div className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
            {kind === "reminder" ? (
              t("portal.calendar.onlyVisibleToYou")
            ) : invitees.length > 0 ? (
              <>
                {t("portal.calendar.create.recipientLabel")}: {invitees.join(", ")}
              </>
            ) : (
              t("portal.calendar.create.recipientNone")
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <Button onClick={handleClose} disabled={submitting}>
            {t("portal.calendar.create.cancel")}
          </Button>
          <Button type="primary" loading={submitting} onClick={() => void handleSubmit()}>
            {isEdit
              ? t("portal.calendar.create.saveChanges")
              : kind === "appointment"
                ? t("portal.calendar.create.submitAppointment")
                : t("portal.calendar.create.submitReminder")}
          </Button>
        </div>
      </div>
    </PortalResponsiveModal>
  )
}
